import { useCallback, useEffect, useState } from 'react'
import View from './view.js'
import { useDataChange, useDataValue } from 'Simple/Data.js'
import getRecipientsQuery from './query-get-recipients.graphql.js'
import getPostalAddressesQuery from './query-get-postal-addresses.graphql.js'
import { useClient } from 'Data/Api'
import { transformRecipients, transformPostalAddresses } from './helpers.js'

export default function Logic(props) {
  let client = useClient()

  let recipient_id = useDataValue({
    context: 'picker',
    path: 'recipient_id',
    viewPath: props.viewPath,
  })

  let change = useDataChange({
    context: 'picker',
    viewPath: props.viewPath,
  })

  let fetchRecipients = useCallback(
    async patient_id => {
      let { data } = await client
        .query(getRecipientsQuery, {
          patient_id,
        })
        .toPromise()

      return transformRecipients(data?.vaxiom_patients_by_pk)
    },
    [client]
  )

  let fetchPostalAddresses = useCallback(
    async person_id => {
      let { data } = await client
        .query(getPostalAddressesQuery, {
          person_id,
        })
        .toPromise()

      return transformPostalAddresses(data?.vaxiom_contact_method_associations)
    },
    [client]
  )

  let autoSelect = useCallback(async () => {
    if (!props.autoSelect) {
      return
    }

    let recipients = await fetchRecipients(props.patientId)

    if (
      recipients.length !== 1 &&
      !recipients.find(r => r.id === props.recipientId)
    ) {
      return {
        recipient_id: null,
        postal_address_id: null,
      }
    }

    let recipient_id = recipients[0].id

    let postal_addresses = await fetchPostalAddresses(recipient_id)

    if (postal_addresses.length <= 1) {
      return {
        recipient_id,
        postal_address_id: postal_addresses[0]?.id || null,
      }
    }
  }, [props.patientId, props.autoSelect, fetchRecipients, fetchPostalAddresses])

  // auto select & save on mount or patientId change
  useEffect(() => {
    let cancelled = false

    ;(async () => {
      let selected = await autoSelect()
      if (cancelled) {
        return
      }

      if (selected && typeof props.onSave === 'function') {
        props.onSave(selected.recipient_id, selected.postal_address_id)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [props.patientId])

  // fetch recipients when patientId changes
  // auto select if only one recipient
  useEffect(() => {
    if (!props.patientId) {
      change(next => {
        next.recipients = []
      })
      return
    }

    let cancelled = false

    ;(async () => {
      let recipients = await fetchRecipients(props.patientId)
      if (cancelled) {
        return
      }

      change(next => {
        next.recipients = recipients
        if (recipients.find(r => r.id === props.recipientId)) {
          next.recipient_id = props.recipientId
        } else {
          next.recipient_id = recipients.length === 1 ? recipients[0].id : null
        }
      })
    })()

    return () => {
      cancelled = true
    }
  }, [client, props.patientId, props.recipientId])

  // fetch postal addresses when recipientId changes
  // auto select if only one postal address
  useEffect(() => {
    if (!recipient_id) {
      change(next => {
        next.postal_addresses = []
      })
      return
    }

    let cancelled = false

    ;(async () => {
      let postal_addresses = await fetchPostalAddresses(recipient_id)
      if (cancelled) {
        return
      }
      change(next => {
        next.postal_addresses = postal_addresses
        if (postal_addresses.find(p => p.id === props.postalAddressId)) {
          next.postal_address_id = props.postalAddressId || null
        } else {
          next.postal_address_id =
            postal_addresses.length === 1 ? postal_addresses[0].id : null
        }
      })
    })()

    return () => {
      cancelled = true
    }
  }, [client, recipient_id])

  useEffect(() => {
    ;(async () => {
      let recipients = await fetchRecipients(props.patientId)
      change(next => {
        next.recipients = recipients
      })
    })()
  }, [recipient_id])

  return (
    <View
      {...props}
      ignoreClickOutsideIf={event =>
        !event?.target?.dataset?.['viewPath'] ||
        event.target?.dataset?.['viewPath']?.includes?.(props.viewPath)
      }
    />
  )
}
